/* ==========================================================================
   BN Free Gift & Spend Threshold Rewards
   --------------------------------------------------------------------------
   Behaviour layer for snippets/bn-gift-rewards.liquid.

   The markup itself is rendered server-side by Liquid and lives inside
   #CartDrawerBody-{section_id}, which this theme (FoxTheme) replaces wholesale
   on every cart update. That means every number on screen is recomputed by
   Shopify, not by this file. This file only does the things Liquid cannot:

     1. Add a chosen gift to the cart.
     2. Remove the gift when the customer asks.
     3. Enforce the rules automatically -- drop the gift if the qualifying
        subtotal falls back under the threshold, and clamp the quantity to 1.
     4. Fire the milestone fireworks (second half of this file).

   Pricing is never touched here. The gift is zeroed out by the Shopify
   automatic discount "Spend $100, get a free gift", which is re-validated at
   checkout. If this script ever fails, the worst case is a gift sitting in the
   cart at full price -- never a discount granted that shouldn't be.

   WHY THE FIREWORKS LIVE IN THIS FILE
   -----------------------------------
   They started out in assets/bn-gift-celebrate.js, loaded by the cart page
   section. Then the store switched to cart_type: drawer, and the drawer only
   loads this one script -- so the celebration silently stopped existing in the
   main flow. Merging it here means the drawer and /cart both get it without
   editing any Liquid, and in particular without touching layout/theme.liquid,
   where a mistake takes down every page instead of just this widget.

   assets/bn-gift-celebrate.js is now unreferenced and can be deleted.
   ========================================================================== */

(function () {
  'use strict';

  /* A single in-flight lock shared by every instance. The cart is global state;
     two concurrent writes to it will clobber each other. */
  var busy = false;

  /* Guards against a pathological add/remove loop if the server and the client
     ever disagree about whether the threshold is met. Reset whenever we observe
     a cart that already satisfies the rules. */
  var corrections = 0;
  var MAX_CORRECTIONS = 5;

  var refreshTimer = null;

  function routes() {
    var fox = (window.FoxTheme && window.FoxTheme.routes) || {};
    var shopify = (window.Shopify && window.Shopify.routes) || {};
    var root = (fox.root_url || shopify.root || '/').replace(/\/$/, '');

    return {
      add: fox.cart_add_url || root + '/cart/add',
      change: fox.cart_change_url || root + '/cart/change',
      cart: fox.cart_url || root + '/cart'
    };
  }

  function fetchConfig(type) {
    type = type || 'json';

    if (window.FoxTheme && window.FoxTheme.utils && window.FoxTheme.utils.fetchConfig) {
      return window.FoxTheme.utils.fetchConfig(type);
    }

    return {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/' + type
      }
    };
  }

  /* Ask every cart-aware section on the page to name itself, so Shopify can
     re-render them all in the same round trip. This is the theme's own
     convention -- see CartItems.updateQuantity in assets/cart.js. */
  function bundledSections() {
    var sections = [];

    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', {
        bubbles: true,
        detail: { sections: sections }
      })
    );

    return sections;
  }

  /* Hand the new cart state to the theme so it swaps in the fresh section HTML
     and fires its own `cart:updated` event. Falls back to a full drawer refresh
     if FoxTheme's pubsub isn't there for some reason. */
  function publishCart(cart) {
    var pubsub = window.FoxTheme && window.FoxTheme.pubsub;

    if (pubsub && pubsub.PUB_SUB_EVENTS && pubsub.PUB_SUB_EVENTS.cartUpdate) {
      pubsub.publish(pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cart });
      return;
    }

    document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: false } }));
  }

  function getCart() {
    return fetch(routes().cart + '.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    }).then(function (response) {
      return response.json();
    });
  }

  function addGift(variantId) {
    var body = JSON.stringify({
      items: [{ id: Number(variantId), quantity: 1 }],
      sections: bundledSections(),
      sections_url: window.location.pathname
    });

    return fetch(routes().add, Object.assign({}, fetchConfig(), { body: body }))
      .then(function (response) {
        return response.json();
      })
      .then(function (added) {
        /* Shopify signals add failures with a `status` field rather than a
           non-2xx response, so this has to be checked explicitly. */
        if (added.status) {
          throw new Error(added.description || added.message || 'Could not add gift');
        }

        return getCart().then(function (cart) {
          cart.sections = added.sections;
          publishCart(cart);
        });
      });
  }

  function changeLine(line, quantity) {
    var body = JSON.stringify({
      line: Number(line),
      quantity: Number(quantity),
      sections: bundledSections(),
      sections_url: window.location.pathname
    });

    return fetch(routes().change, Object.assign({}, fetchConfig(), { body: body }))
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        publishCart(cart);
      });
  }

  /* In drawer mode /cart renders the rewards box twice: once in the cart page
     section, once inside the drawer that the overlay group puts on every page.
     Anything that needs to measure or point at the widget must use the copy the
     customer can actually see -- a closed drawer measures 0x0, which is exactly
     how the fireworks failed silently before. Enforcement does not care about
     visibility, so it falls back to whichever copy exists. */
  function visibleRewards() {
    var elements = document.querySelectorAll('bn-gift-rewards');

    for (var i = 0; i < elements.length; i++) {
      var bar = elements[i].querySelector('.bn-rewards__bar');
      if (bar && bar.getBoundingClientRect().width > 0) return elements[i];
    }

    return null;
  }

  function anyRewards() {
    return visibleRewards() || document.querySelector('bn-gift-rewards');
  }

  class BNGiftRewards extends HTMLElement {
    connectedCallback() {
      if (!this.__bnBound) {
        this.__bnBound = true;
        this.addEventListener('click', this.onClick.bind(this));
      }

      this.enforce();
    }

    num(name) {
      const value = parseInt(this.dataset[name], 10);
      return isNaN(value) ? 0 : value;
    }

    /* Reconcile the cart against the reward rules. Runs on every render, which
       covers quantity edits, line removals and discount-code changes alike --
       we never have to enumerate the ways a cart can change. */
    async enforce() {
      const line = this.num('giftLine');
      const extraLine = this.num('giftExtraLine');

      let targetLine = null;
      let targetQty = 0;

      if (line > 0 && this.num('qualifyingTotal') < this.num('giftThreshold')) {
        // Fell back below the threshold -- take the gift back.
        targetLine = line;
      } else if (extraLine > 0) {
        // A second gift line snuck in. Drop them one render at a time; removing
        // a line reindexes the cart, so the next pass re-reads fresh indexes.
        targetLine = extraLine;
      } else if (line > 0 && this.num('giftQty') > 1) {
        // Only one gift is ever free.
        targetLine = line;
        targetQty = 1;
      }

      if (targetLine === null) {
        corrections = 0;
        return;
      }

      if (busy || corrections >= MAX_CORRECTIONS) return;

      corrections++;
      busy = true;

      try {
        await changeLine(targetLine, targetQty);
      } catch (error) {
        console.error('[BN gift rewards] could not adjust gift line:', error);
      } finally {
        busy = false;
      }
    }

    onClick(event) {
      const addButton = event.target.closest('[data-bn-gift-add]');
      if (addButton) {
        event.preventDefault();
        this.handleAdd(addButton);
        return;
      }

      const removeButton = event.target.closest('[data-bn-gift-remove]');
      if (removeButton) {
        event.preventDefault();
        this.handleRemove(removeButton);
        return;
      }

      const toggle = event.target.closest('[data-bn-gift-toggle]');
      if (toggle) {
        event.preventDefault();
        this.classList.toggle('bn-rewards--picker-open');
      }
    }

    async handleAdd(button) {
      if (busy) return;

      const card = button.closest('[data-bn-gift-card]');
      const select = card && card.querySelector('[data-bn-gift-variant]');
      const variantId = select ? select.value : button.getAttribute('data-bn-gift-add');

      if (!variantId) return;

      busy = true;
      corrections = 0;
      button.classList.add('bn-is-loading');
      button.setAttribute('disabled', 'disabled');

      try {
        await addGift(variantId);
      } catch (error) {
        console.error('[BN gift rewards] could not add gift:', error);
        button.classList.remove('bn-is-loading');
        button.removeAttribute('disabled');
      } finally {
        busy = false;
      }
    }

    async handleRemove(button) {
      if (busy) return;

      const line = this.num('giftLine');
      if (!line) return;

      busy = true;
      corrections = 0;
      button.classList.add('bn-is-loading');

      try {
        await changeLine(line, 0);
      } catch (error) {
        console.error('[BN gift rewards] could not remove gift:', error);
        button.classList.remove('bn-is-loading');
      } finally {
        busy = false;
      }
    }
  }

  if (!customElements.get('bn-gift-rewards')) {
    customElements.define('bn-gift-rewards', BNGiftRewards);
  }

  /* ======================================================================
     Milestone fireworks
     ----------------------------------------------------------------------
     Fires when the cart crosses $100 or $200. Decorative only -- it reads the
     data attributes Liquid already renders and never touches the cart.

     Fires on the crossing, not on the state. Celebrated milestones are kept in
     sessionStorage so a reload, or coming back from checkout, does not repeat
     the burst. Dropping back below a threshold clears that flag, so genuinely
     re-earning it celebrates again.

     A milestone is only banked once the burst actually rendered. If the widget
     was not visible at the time -- drawer mid-slide, or the line was crossed
     from a product page -- the flag stays armed and it fires at the next
     opportunity. That is why the check runs twice per cart update.
     ====================================================================== */

  var STORE_KEY = 'bn-gift-milestones';

  var TIERS = {
    gift: {
      /* Position along the bar, matching the dots the snippet places at 50%
         and 100%. */
      at: 0.5,
      shells: 1,
      particles: 70,
      power: 5.4,
      colors: ['#1f9d55', '#4ade80', '#a7f3d0', '#ffffff']
    },
    discount: {
      at: 1,
      shells: 3,
      particles: 90,
      power: 6.6,
      colors: ['#d99b1c', '#fbbf24', '#fde68a', '#ffffff', '#1f9d55']
    }
  };

  var GRAVITY = 0.045;
  var DRAG = 0.985;
  var FADE = 0.012;

  var canvas = null;
  var ctx = null;
  var particles = [];
  var frame = null;

  function reducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function readState() {
    try {
      return JSON.parse(sessionStorage.getItem(STORE_KEY)) || {};
    } catch (error) {
      /* Private mode, blocked storage, corrupted value -- all non-fatal. The
         worst outcome is a repeated firework, so there is nothing to report. */
      return {};
    }
  }

  function writeState(state) {
    try {
      sessionStorage.setItem(STORE_KEY, JSON.stringify(state));
    } catch (error) {
      /* Ignore -- see readState. */
    }
  }

  function ensureCanvas() {
    if (canvas) return;

    canvas = document.createElement('canvas');
    canvas.className = 'bn-fx-canvas';
    canvas.setAttribute('aria-hidden', 'true');
    document.body.appendChild(canvas);

    ctx = canvas.getContext('2d');
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
  }

  function resizeCanvas() {
    if (!canvas) return;

    var ratio = window.devicePixelRatio || 1;
    canvas.width = window.innerWidth * ratio;
    canvas.height = window.innerHeight * ratio;
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
  }

  function teardown() {
    if (frame) cancelAnimationFrame(frame);
    frame = null;
    particles = [];

    window.removeEventListener('resize', resizeCanvas);

    if (canvas && canvas.parentNode) canvas.parentNode.removeChild(canvas);
    canvas = null;
    ctx = null;
  }

  function explode(x, y, tier) {
    for (var i = 0; i < tier.particles; i++) {
      /* Even angular spread with a jittered radius gives a rounder shell than
         randomising the angle, which tends to clump. */
      var angle = (Math.PI * 2 * i) / tier.particles + Math.random() * 0.12;
      var speed = tier.power * (0.45 + Math.random() * 0.55);

      particles.push({
        x: x,
        y: y,
        px: x,
        py: y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 1,
        decay: FADE * (0.7 + Math.random() * 0.8),
        size: 1 + Math.random() * 1.6,
        color: tier.colors[(Math.random() * tier.colors.length) | 0]
      });
    }
  }

  function tick() {
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    for (var i = particles.length - 1; i >= 0; i--) {
      var p = particles[i];

      p.px = p.x;
      p.py = p.y;
      p.vx *= DRAG;
      p.vy = p.vy * DRAG + GRAVITY;
      p.x += p.vx;
      p.y += p.vy;
      p.life -= p.decay;

      if (p.life <= 0) {
        particles.splice(i, 1);
        continue;
      }

      /* Drawn as a short segment from the previous position rather than a dot,
         which reads as a spark trail without the cost of a real trail buffer. */
      ctx.globalAlpha = Math.max(p.life, 0);
      ctx.strokeStyle = p.color;
      ctx.lineWidth = p.size;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(p.px, p.py);
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
    }

    ctx.globalAlpha = 1;

    if (particles.length) {
      frame = requestAnimationFrame(tick);
    } else {
      teardown();
    }
  }

  /* Returns true only if the burst actually went out, so the caller knows
     whether it may bank the milestone. */
  function celebrate(name) {
    var tier = TIERS[name];
    if (!tier || document.hidden) return false;

    var element = visibleRewards();
    if (!element) return false;

    var bar = element.querySelector('.bn-rewards__bar');
    if (!bar) return false;

    var rect = bar.getBoundingClientRect();
    if (!rect.width) return false;

    /* Reduced motion still counts as celebrated -- the customer opted out of
       the animation, not out of the milestone. Returning false here would
       retry on every cart update forever. */
    if (reducedMotion()) return true;

    var x = rect.left + rect.width * tier.at;
    var y = rect.top + rect.height / 2;

    /* Pulse the milestone dot itself so the win is legible even to someone who
       looks up after the sparks have gone. */
    var dots = element.querySelectorAll('.bn-rewards__node');
    var dot = name === 'discount' ? dots[dots.length - 1] : dots[0];
    if (dot) {
      dot.classList.remove('bn-is-popping');
      void dot.offsetWidth;
      dot.classList.add('bn-is-popping');
    }

    ensureCanvas();

    for (var shell = 0; shell < tier.shells; shell++) {
      if (shell === 0) {
        explode(x, y, tier);
      } else {
        /* Extra shells drift up and out so three bursts do not stack into one
           blob. Offsets are clamped to the viewport. */
        (function (index) {
          setTimeout(function () {
            ensureCanvas();

            var spread = rect.width * 0.28;
            var ox = Math.min(Math.max(x + (index % 2 === 0 ? -spread : spread), 20), window.innerWidth - 20);
            var oy = Math.max(y - 40 - index * 28, 20);

            explode(ox, oy, tier);

            if (!frame) frame = requestAnimationFrame(tick);
          }, index * 260);
        })(shell);
      }
    }

    if (!frame) frame = requestAnimationFrame(tick);
    return true;
  }

  function readNum(element, key) {
    var value = parseInt(element.dataset[key], 10);
    return isNaN(value) ? 0 : value;
  }

  function checkMilestones() {
    var element = anyRewards();
    if (!element) return;

    var total = readNum(element, 'qualifyingTotal');
    var reached = {
      gift: total >= readNum(element, 'giftThreshold'),
      discount: total >= readNum(element, 'discountThreshold')
    };

    var previous = readState();
    var next = {};

    Object.keys(reached).forEach(function (name) {
      if (!reached[name]) {
        next[name] = false;
      } else if (previous[name]) {
        next[name] = true;
      } else {
        /* Newly crossed. Only bank it if the burst rendered, otherwise leave it
           armed for the next chance. */
        next[name] = celebrate(name);
      }
    });

    writeState(next);
  }

  /* connectedCallback fires while the theme is still mid-update (and therefore
     while `busy` is true), so a freshly rendered element can skip its own
     enforce pass. This catches it once the dust settles. */
  document.addEventListener('cart:updated', function () {
    clearTimeout(refreshTimer);

    refreshTimer = setTimeout(function () {
      var element = anyRewards();
      if (element && typeof element.enforce === 'function') element.enforce();
    }, 80);

    /* Twice: once immediately for the cart page, once after the drawer has had
       time to slide open. The banking logic makes the second pass a no-op if
       the first already fired. */
    setTimeout(checkMilestones, 0);
    setTimeout(checkMilestones, 450);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', checkMilestones);
  } else {
    checkMilestones();
  }
})();
